/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React, { useState, useEffect } from 'react';
import { X, Check, Loader2, Briefcase, BookOpen, Users, Star, Target, Award, TrendingUp } from 'lucide-react';
import './FocusAreaSelector.css';

const CATEGORY_ICONS = {
  career: Briefcase,
  learning: BookOpen,
  networking: Users,
  hiring: Target,
  training: Award,
  partnership: TrendingUp,
  placements: Briefcase,
  default: Star
};

const FocusAreaSelector = ({ entityType, onClose, onSave }) => {
  const [focusAreas, setFocusAreas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFocusAreas();
  }, [entityType]);

  const fetchFocusAreas = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/focus-areas?entityType=${entityType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load focus areas');

      const data = await response.json();
      const areas = data.focusAreas || [];
      setFocusAreas(areas);

      // Initialize selected state from API response
      const selected = {};
      areas.forEach(area => {
        if (area.isSelected) {
          selected[area.id] = area.userPriority || area.default_priority;
        }
      });
      setSelectedAreas(selected);
    } catch (err) {
      setError('Failed to load focus areas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFocusArea = (areaId, defaultPriority) => {
    setSelectedAreas(prev => {
      if (prev[areaId]) {
        const { [areaId]: removed, ...rest } = prev;
        return rest;
      } else {
        return { ...prev, [areaId]: defaultPriority };
      }
    });
  };

  const updatePriority = (areaId, priority) => {
    setSelectedAreas(prev => ({
      ...prev,
      [areaId]: parseInt(priority)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Convert to array format for API
      const focusAreaData = Object.entries(selectedAreas).map(([focusAreaId, priority]) => ({
        focusAreaId,
        priority
      }));

      const token = localStorage.getItem('token');
      const response = await fetch('/api/focus-areas/my', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ focusAreas: focusAreaData })
      });

      if (!response.ok) throw new Error('Failed to save focus areas');

      onSave();
    } catch (err) {
      setError('Failed to save focus areas');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Group by category
  const groupedAreas = focusAreas.reduce((acc, area) => {
    const category = area.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(area);
    return acc;
  }, {});

  const getPriorityColor = (priority) => {
    if (priority >= 8) return 'priority-high';
    if (priority >= 5) return 'priority-medium';
    return 'priority-low';
  };

  if (loading) {
    return (
      <div className="focus-area-modal-overlay">
        <div className="focus-area-modal focus-area-loading">
          <Loader2 className="spinner" size={32} />
          <p>Loading focus areas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="focus-area-modal-overlay">
      <div className="focus-area-modal">
        {/* Header */}
        <div className="focus-area-header">
          <div>
            <h2>Customize Your Focus</h2>
            <p>Select areas to prioritize and set their importance (1-10)</p>
          </div>
          <button onClick={onClose} className="close-btn">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="focus-area-content">
          {error && (
            <div className="focus-area-error">
              {error}
            </div>
          )}

          {Object.entries(groupedAreas).map(([category, areas]) => {
            const CategoryIcon = CATEGORY_ICONS[category] || CATEGORY_ICONS.default;
            return (
              <div key={category} className="focus-category">
                <div className="category-header">
                  <CategoryIcon size={16} />
                  <h3>{category.charAt(0).toUpperCase() + category.slice(1)}</h3>
                </div>

                <div className="focus-area-list">
                  {areas.map(area => {
                    const isSelected = !!selectedAreas[area.id];
                    const priority = selectedAreas[area.id] || area.default_priority;

                    return (
                      <div
                        key={area.id}
                        className={`focus-area-item ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="focus-area-main">
                          <button
                            onClick={() => toggleFocusArea(area.id, area.default_priority)}
                            className={`checkbox ${isSelected ? 'checked' : ''}`}
                          >
                            {isSelected && <Check size={14} />}
                          </button>
                          <div className="focus-area-info">
                            <span className="focus-area-name">{area.name}</span>
                            <span className="focus-area-desc">{area.description}</span>
                          </div>
                        </div>

                        {isSelected && (
                          <div className="priority-control">
                            <label>Priority:</label>
                            <input
                              type="range"
                              min="1"
                              max="10"
                              value={priority}
                              onChange={(e) => updatePriority(area.id, e.target.value)}
                              className="priority-slider"
                            />
                            <span className={`priority-value ${getPriorityColor(priority)}`}>
                              {priority}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="focus-area-footer">
          <p className="selected-count">
            {Object.keys(selectedAreas).length} focus areas selected
          </p>
          <div className="footer-actions">
            <button onClick={onClose} className="btn-cancel">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-save"
            >
              {saving && <Loader2 className="spinner" size={16} />}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FocusAreaSelector;
